import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/store/themeStore';
import type { Theme } from '@/lib/theme/applyTheme';
import type { PriceHistoryPointDto } from '@/lib/models';
import { MarketPriceChart, type MarketChartLine } from './MarketPriceChart';
import { OutcomePriceHistoryLoader } from './OutcomePriceHistoryLoader';

/** One outcome to chart: a stable key + the local outcome id its price history loads from. */
export interface ChartOutcome {
  id: string;
  outcomeId: string;
  /** Series display name (candidate / team); falls back to "Yes" for a pure Yes/No bar. */
  label?: string | null;
  /** Explicit line colour (e.g. a sports team colour); falls back to the palette when absent. */
  color?: string | null;
  /**
   * Current Yes price in whole cents (the option row's seed price). Anchors the
   * line's right end at "now" so it matches the buy bar; omit on closed/settled
   * charts to freeze the line at its last trade.
   */
  currentPriceCents?: number | null;
}

export interface MarketChartContainerProps {
  /** Outcomes whose Yes-price lines to chart (one line each). */
  outcomes: ChartOutcome[];
}

/** Selectable chart windows (Figma 1D / 1W / 1M / All). */
type ChartTimeframe = '1D' | '1W' | '1M' | 'All';
const TIMEFRAMES: ChartTimeframe[] = ['1D', '1W', '1M', 'All'];

const DAY = 24 * 60 * 60;
/**
 * Per-timeframe window (seconds) + bucket size. Plaee's `period_interval` is in
 * MINUTES (capped at 1440 = 24h); the window (startTs/endTs) is in seconds.
 */
const TIMEFRAME_CONFIG: Record<ChartTimeframe, { windowSeconds: number; periodInterval: number }> = {
  '1D': { windowSeconds: DAY, periodInterval: 60 }, // hourly
  '1W': { windowSeconds: 7 * DAY, periodInterval: 360 }, // 6h buckets
  '1M': { windowSeconds: 30 * DAY, periodInterval: 1440 }, // daily
  All: { windowSeconds: 365 * DAY, periodInterval: 1440 }, // daily, ~1y
};

/**
 * Multi-line outcome palette, favourites-first order, per theme. Light keeps
 * the Figma values; dark re-steps the same hue families into the dark-surface
 * lightness band (OKLCH L 0.48–0.67, staggered so adjacent slots stay apart
 * for colorblind viewers — the last slot swaps green→teal, since a red↔green
 * neighbour pair is indistinguishable under protanopia at any lightness).
 */
const PALETTES: Record<Theme, string[]> = {
  light: ['#87BFFF', '#4378FF', '#FDC503', '#FF7F0E', '#FF5952', '#12B76A'],
  dark: ['#5395DF', '#2E5FE4', '#B78B00', '#AD4200', '#FA4B5B', '#00A396'],
};

/**
 * Max lines plotted. Callers pass outcomes ranked top-first (by chance), so an
 * event with many markets/options (e.g. a 19-candidate winner) charts only its
 * top contenders — bounding both the rendered lines and the per-line
 * price-history fetches.
 */
const MAX_CHART_LINES = 3;

/**
 * Data-fetching wrapper for the market price chart (ARC-82 §5.2).
 *
 * Mounts one {@link OutcomePriceHistoryLoader} per charted outcome over the
 * selected window (1D/1W/1M/All), aggregates the histories, and feeds the
 * single-area / multi-line {@link MarketPriceChart}. A binary market is one line
 * (area); a multi-outcome market charts its top {@link MAX_CHART_LINES} options.
 */
export function MarketChartContainer({ outcomes }: MarketChartContainerProps) {
  const palette = PALETTES[useTheme()];
  const [historyByOutcome, setHistoryByOutcome] = useState<Record<string, PriceHistoryPointDto[]>>({});
  const [timeframe, setTimeframe] = useState<ChartTimeframe>('All');
  // Freeze "now" at mount so re-renders don't shift the query keys. Lazy
  // initialiser keeps the impure `Date.now()` read out of the render body.
  const [now] = useState(() => Math.floor(Date.now() / 1000));

  function handleLoaded(outcomeId: string, history: PriceHistoryPointDto[]) {
    setHistoryByOutcome((prev) => (prev[outcomeId] === history ? prev : { ...prev, [outcomeId]: history }));
  }

  /** Switch window and drop stale buckets so the chart redraws from the new range. */
  function handleTimeframe(tf: ChartTimeframe) {
    if (tf === timeframe) return;
    setHistoryByOutcome({});
    setTimeframe(tf);
  }

  const { windowSeconds, periodInterval } = TIMEFRAME_CONFIG[timeframe];
  const startTs = now - windowSeconds;
  const charted = outcomes.slice(0, MAX_CHART_LINES);

  const lines: MarketChartLine[] = charted.map((o, i) => ({
    id: o.id,
    label: o.label || 'Yes',
    color: o.color ?? palette[i % palette.length],
    history: historyByOutcome[o.outcomeId] ?? [],
    currentPrice: o.currentPriceCents != null ? o.currentPriceCents / 100 : null,
  }));

  return (
    <div className="flex flex-col gap-2">
      <div className="relative w-full">
        {charted.map((o) => (
          <OutcomePriceHistoryLoader
            key={`${o.outcomeId}-${timeframe}`}
            outcomeId={o.outcomeId}
            startTs={startTs}
            endTs={now}
            periodInterval={periodInterval}
            onLoaded={handleLoaded}
          />
        ))}
        {lines.length > 0 ? (
          <MarketPriceChart
            key={timeframe}
            lines={lines}
            // "All" starts at the earliest trade; fixed windows span exactly their timeframe.
            domainStartTs={timeframe === 'All' ? null : startTs}
            domainEndTs={now}
          />
        ) : (
          <div className="flex h-[240px] items-center justify-center text-sm text-muted">No price history yet.</div>
        )}
      </div>

      <div role="tablist" aria-label="Chart timeframe" className="flex h-9 items-center justify-end self-end">
        {TIMEFRAMES.map((tf) => {
          const active = tf === timeframe;
          return (
            <button
              key={tf}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => handleTimeframe(tf)}
              className={cn(
                'h-full cursor-pointer px-1.5 text-sm font-semibold uppercase transition-colors',
                active ? 'text-primary' : 'text-muted hover:text-primary',
              )}
            >
              {tf}
            </button>
          );
        })}
      </div>
    </div>
  );
}
